const VERSION_KEY = "ttt_app_build";
const RELOAD_ONCE_KEY = "ttt_cache_reload_once";

/**
 * Unregister service workers and wipe Cache API storage (Workbox, PWA, etc.).
 */
export async function purgeAllClientCaches() {
  if (typeof window === "undefined") return;

  if ("serviceWorker" in navigator) {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => registration.unregister()));
    } catch {
      /* ignore */
    }
  }

  if ("caches" in window) {
    try {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
    } catch {
      /* ignore */
    }
  }
}

function isReloadPending() {
  try {
    return sessionStorage.getItem(RELOAD_ONCE_KEY) === "1";
  } catch {
    return false;
  }
}

function markReloadPending() {
  try {
    sessionStorage.setItem(RELOAD_ONCE_KEY, "1");
  } catch {
    /* ignore */
  }
}

function clearReloadPending() {
  try {
    sessionStorage.removeItem(RELOAD_ONCE_KEY);
  } catch {
    /* ignore */
  }
}

function storeBuildId(build) {
  try {
    localStorage.setItem(VERSION_KEY, build);
  } catch {
    /* ignore */
  }
}

function getStoredBuildId() {
  try {
    return localStorage.getItem(VERSION_KEY);
  } catch {
    return null;
  }
}

/** True when index.html still points at an old hashed bundle. */
export function htmlScriptMatchesVersion(mainJs) {
  if (!mainJs || typeof document === "undefined") return true;

  const expected = String(mainJs).replace(/^\//, "");
  const scripts = document.getElementsByTagName("script");

  for (let i = 0; i < scripts.length; i += 1) {
    const src = scripts[i].getAttribute("src") || "";
    if (src.includes("/static/js/main.") && src.includes(expected)) {
      return true;
    }
  }

  return false;
}

async function fetchBuildVersion() {
  const response = await fetch(`/version.json?_=${Date.now()}`, {
    cache: "no-store",
    headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
  });
  if (!response.ok) return null;
  const data = await response.json();
  if (!data?.build) return null;
  return data;
}

function scheduleHardReload(build) {
  if (build) storeBuildId(build);
  markReloadPending();
  window.location.reload();
}

/**
 * Always purge client caches, then reload if deploy changed or index.html is stale.
 * Resolves when it is safe to boot React.
 */
export async function ensureFreshAppCache() {
  if (typeof window === "undefined") return;

  await purgeAllClientCaches();

  if (isReloadPending()) {
    clearReloadPending();
    return;
  }

  let versionData;
  try {
    versionData = await fetchBuildVersion();
  } catch {
    return;
  }
  if (!versionData) return;

  const { build, mainJs } = versionData;
  const storedBuild = getStoredBuildId();
  const staleHtml = !htmlScriptMatchesVersion(mainJs);
  const staleBuild = Boolean(storedBuild && storedBuild !== build);

  if (staleHtml || staleBuild) {
    scheduleHardReload(build);
    await new Promise(() => {});
    return;
  }

  if (!storedBuild) {
    storeBuildId(build);
  }
}

/** Re-check when a phone restores a frozen tab from back/forward cache. */
export function registerCacheHygiene() {
  if (typeof window === "undefined") return;

  window.addEventListener("pageshow", (event) => {
    if (!event.persisted) return;
    ensureFreshAppCache().catch(() => {});
  });

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState !== "visible") return;
    purgeAllClientCaches().catch(() => {});
  });
}

export async function purgeCachesOnLogout() {
  const { clearAllMv2PersistedCaches } = await import("@/features/_mv2/mv2CachePersist");
  clearAllMv2PersistedCaches();
  await purgeAllClientCaches();
}
